import $ from '@sparta.global.vendors/jquery';
import DataAssetLoader from '@sparta.global.utilities/data-asset-loader-utility/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaGlobalFunctionsUtility from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';

/**
 * @module
 * @name sparta-browser-support-utility
 * @desc export a singleton instance of SpartaBrowserSupportUtility class
 * @version 4.4.0
 */

const defaults = {
  userAgent: null,
  showMessage: true,
  // turn off message
  useAjax: false,
  // use ajax to get updated browser rules
  debug: false,
  // enable debugging
  testMode: false,
  // test mode; turns on messaging on command
  isLocal: spartaStoreUtility.getData('environment') === 'LOCAL',
  useSessionStorage: spartaStoreUtility.getData('useSessionStorage'),
  // Is Session Storage Available
  ua: '',
  platform: '',
  boaBrowserShowNoticeCookie: '',
  boaBrowserTestCookie: ''
};
const EVENTS = {
  LOADED: 'LOADED.SpartaBrowserSupportUtility'
};
const VERSIONS = {
  BLOCKED_IE: 11,
  BLOCKED_FF: 44,
  EOL_IE: 11,
  EOL_EDGE: 96,
  EOL_CHROME: 93,
  EOL_FF: 93,
  EOL_SAFARI: 14.0
};
const TLS_COOKIE_NAME = 'TLS';
const TLS_COOKIE_VALUE_VERSION_PREFIX = 'TLSv';
const MIN_SUPPORTED_TLS_VERSION = 1.2;
let instance;

/**
 * @class SpartaBrowserSupportUtility
 * @classdesc Class for global implementation of browser detection
 * @returns {void}
 */
class SpartaBrowserSupportUtility {
  /**
   * @method
   * @name constructor
   * @desc Initialize an instance of our browser support class
   * @param {object} options - Configuration data.
   * @returns {object} existing instance or creates one.
   */
  constructor(options = {}) {
    if (!instance) {
      try {
        this.settings = $.extend({}, defaults, options);
        this.debug('browserDetect: begin browserDetect init');

        // Accept user agent for testing, or get property from browser.
        // If UA was passed in (debug mode), disable caching.
        this.settings.useSessionStorage = this.settings.userAgent ? false : this.settings.useSessionStorage;
        this.settings.ua = this.settings.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
        this.settings.platform = typeof navigator !== 'undefined' ? navigator.platform : '';
        this.settings.boaBrowserShowNoticeCookie = spartaGlobalFunctionsUtility.spartaGetCookie('boaBrowserShowNotice');

        // Enable Test mode for dev by setting cookie, or this.settings.testMode=true.
        this.settings.boaBrowserTestCookie = spartaGlobalFunctionsUtility.spartaGetCookie('boaBrowserTest');
        if (this.settings.boaBrowserTestCookie) {
          this.settings.testMode = true;
        }

        // If `testMode` is on, check for cookie enabling browser message, disable session storage.
        if (this.settings.testMode) {
          this.settings.debug = true; // enable debug
          this.settings.showMessage = true; // show message on all pages
          this.settings.useSessionStorage = false; // don't use session storage
          this.debug(`browserDetect: testMode is: ${this.settings.testMode}`);
        }

        // Check local storage first.
        this.boaBrowserObject = this.boaBrowserGetSessionStorage();

        // If browser object found in local session, just show message.
        if (this.boaBrowserObject && this.boaBrowserObject.browserSupport) {
          // Show browser message after doc ready.
          this.boaBrowserShowMessage();

          // Set loaded complete flag.
          this.boaBrowserObject.loaded = true;
          // Set loaded complete flag.
          spartaEventsUtility.pub(EVENTS.LOADED);
        } else {
          // If not in session, create fresh browser object.
          // Define default browser object.
          this.boaBrowserGetDefaultObj(boaBrowserDefault => {
            // Run Bowser detection.
            const bowser = this.bowserUtil(this.settings.ua);

            // Merge Bowser result with default browser object.
            this.boaBrowserObject = $.extend(true, boaBrowserDefault, bowser);

            // Add system check data.
            this.boaBrowserSystemCheck();

            // Add plugin check data.
            this.boaBrowserPluginCheck();
            spartaStoreUtility.addData('browser', this.boaBrowserObject);

            // Browser Messaging Rules.
            this.boaBrowserUpdateMessageData();
            this.debug('browserDetect: end browserDetect init');
            this.debug('this.boaBrowserObject');
            this.debug(this.boaBrowserObject);
          });
        }
      } catch (e) {
        this.debug(`browserDetect: _initialize error: ${e}`);
      }

      // Run debugging mode.
      /* if (
        this.boaBrowserObject
        && this.boaBrowserObject.debug
        && typeof boaBrowserDebug === 'function'
      ) {
        try {
          setTimeout(function () {
            boaBrowserDebug();
          }, 1000);
        } catch (e) {
          this.debug(`browserDetect: _initialize error: ${e}`);
        }
      } */
      instance = this;
    } else {
      return instance;
    }
  }

  /**
   * Events function.
   */
  get EVENTS() {
    return EVENTS;
  }

  /**
   * Hide Browser Alert function.
   * @returns {void}
   */
  hideBrowserAlert() {
    this.debug('begin hideBrowserAlert()');
    this.settings.showMessage = false;
  }

  /**
   * Debug function.
   * @param {*} msg debug string.
   * @returns {void}
   */
  debug(msg) {
    if (window.console && this.settings.debug) {
      // eslint-disable-next-line no-console
      console.log(msg);
    }
  }

  /**
   * Start Bowser detect function.
   * Bowser is a standalone utility providing browser and mobile device detection. If Bowser is
   * updated with new code, it can easily be swapped out in `bowserUtil` without affecting any
   * other logic.
   * @param {*} uaString User agent string.
   * @returns {void}
   */
  bowserUtil(uaString) {
    /*!
     * Bowser - a browser detector
     * https://github.com/ded/bowser
     * MIT License | (c) Dustin Diaz 2014
     */

    const t = true;

    /**
     * Detect user agent string.
     * @param {*} ua User agent string.
     * @returns {void}
     */
    function detect(ua) {
      /**
       * First match in agent string.
       * @param {*} regex User agent regex.
       * @returns {void}
       */
      function getFirstMatch(regex) {
        const match = ua.match(regex);
        return match && match.length > 1 && match[1] || '';
      }
      const iosdevice = getFirstMatch(/(ipod|iphone|ipad)/i).toLowerCase();
      const likeAndroid = /like android/i.test(ua);
      const android = !likeAndroid && /android/i.test(ua);
      const silk = /silk/i.test(ua);
      // const sailfish = /sailfish/i.test(ua);
      // const tizen = /tizen/i.test(ua);
      // const webos = /(web|hpw)os/i.test(ua);
      const windowsphone = /windows phone/i.test(ua);
      const edgeVersion = getFirstMatch(/edg\/(\d+(\.\d+)?)/i);
      const versionIdentifier = getFirstMatch(/version\/(\d+(\.\d+)?)/i);
      const tabletPc = /tablet pc/i.test(ua);
      const tablet = /tablet/i.test(ua) && !tabletPc;
      const mobile = !tablet && /[^-]mobi/i.test(ua);
      const windows = !windowsphone && /windows/i.test(ua);
      const mac = !iosdevice && !silk && /macintosh/i.test(ua);
      const linux = !android && /linux/i.test(ua);
      const touchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
      let result;
      if (/googlebot|bingbot|ysearch\/slurp|facebookexternalhit|twitterbot|BofASpider/i.test(ua)) {
        result = {
          name: 'Crawler/Bot',
          crawler: t
        };
      } else if (/opera|opr/i.test(ua)) {
        result = {
          name: 'Opera',
          opera: t,
          version: versionIdentifier || getFirstMatch(/(?:opera|opr)[\s/](\d+(\.\d+)?)/i)
        };
      } else if (windowsphone) {
        result = {
          name: 'Windows Phone',
          vendor: 'Microsoft',
          windowsphone: t,
          msie: t
        };
        if (edgeVersion) {
          result.msedge = t;
          result.version = edgeVersion;
        } else {
          result.version = getFirstMatch(/iemobile\/(\d+(\.\d+)?)/i);
        }
      } else if (/maxthon/i.test(ua)) {
        result = {
          name: 'Maxthon',
          maxthon: t,
          version: getFirstMatch(/maxthon\/(\d+(\.\d+)?)/i)
        };
      } else if (/chrom.+? edg/i.test(ua)) {
        result = {
          name: 'Edge',
          vendor: 'Microsoft',
          msie: t,
          edge: t,
          msedge: t,
          version: edgeVersion
        };
      } else if (/msie|trident/i.test(ua)) {
        result = {
          name: 'Internet Explorer',
          vendor: 'Microsoft',
          msie: t,
          version: getFirstMatch(/(?:msie |rv:)(\d+(\.\d+)?)/i)
        };
      } else if (/chrome|crios|crmo/i.test(ua)) {
        result = {
          name: 'Chrome',
          vendor: 'Google',
          chrome: t,
          version: getFirstMatch(/(?:chrome|crios|crmo)\/(\d+(\.\d+)?)/i)
        };
      } else if (iosdevice) {
        result = {
          // eslint-disable-next-line no-nested-ternary
          name: iosdevice === 'iphone' ? 'iPhone' : iosdevice === 'ipad' ? 'iPad' : 'iPod',
          vendor: 'Apple'
        };
        // Version is not part of user agent in web apps.
        if (versionIdentifier) {
          result.version = versionIdentifier;
        }
      } else if (/sailfish/i.test(ua)) {
        result = {
          name: 'Sailfish',
          sailfish: t,
          version: getFirstMatch(/sailfish\s?browser\/(\d+(\.\d+)?)/i)
        };
      } else if (/seamonkey\//i.test(ua)) {
        result = {
          name: 'SeaMonkey',
          seamonkey: t,
          version: getFirstMatch(/seamonkey\/(\d+(\.\d+)?)/i)
        };
      } else if (/firefox|iceweasel/i.test(ua)) {
        result = {
          name: 'Firefox',
          vendor: 'Mozilla',
          firefox: t,
          version: getFirstMatch(/(?:firefox|iceweasel)[ /](\d+(\.\d+)?)/i)
        };
        if (/\((mobile|tablet);[^)]*rv:[\d.]+\)/i.test(ua)) {
          result.firefoxos = t;
        }
      } else if (/silk/i.test(ua)) {
        result = {
          name: 'Silk',
          vendor: 'Amazon',
          silk: t,
          version: getFirstMatch(/silk\/(\d+(\.\d+)?)/i)
        };
      } else if (android) {
        result = {
          name: 'Android',
          vendor: 'Google',
          version: versionIdentifier
        };
      } else if (/phantom/i.test(ua)) {
        result = {
          name: 'PhantomJS',
          phantom: t,
          version: getFirstMatch(/phantomjs\/(\d+(\.\d+)?)/i)
        };
      } else if (/blackberry|\bbb\d+/i.test(ua) || /rim\stablet/i.test(ua)) {
        result = {
          name: 'BlackBerry',
          blackberry: t,
          version: versionIdentifier || getFirstMatch(/blackberry[\d]+\/(\d+(\.\d+)?)/i)
        };
      } else if (/(web|hpw)os/i.test(ua)) {
        result = {
          name: 'WebOS',
          webos: t,
          version: versionIdentifier || getFirstMatch(/w(?:eb)?osbrowser\/(\d+(\.\d+)?)/i)
        };
        if (/touchpad\//i.test(ua)) {
          result.touchpad = t;
        }
      } else if (/bada/i.test(ua)) {
        result = {
          name: 'Bada',
          bada: t,
          version: getFirstMatch(/dolfin\/(\d+(\.\d+)?)/i)
        };
      } else if (/tizen/i.test(ua)) {
        result = {
          name: 'Tizen',
          tizen: t,
          version: getFirstMatch(/(?:tizen\s?)?browser\/(\d+(\.\d+)?)/i) || versionIdentifier
        };
      } else if (/safari/i.test(ua)) {
        result = {
          name: 'Safari',
          vendor: 'Apple',
          safari: t,
          version: versionIdentifier
        };
      } else {
        result = {};
      }

      // Set Webkit or Gecko flag for browsers based on these engines.
      if (!result.msedge && /(apple)?webkit/i.test(ua)) {
        if (/(apple)?webkit\/537\.36/i.test(ua)) {
          result.name = result.name || 'Blink';
          result.blink = t;
        } else {
          result.name = result.name || 'Webkit';
          result.webkit = t;
        }
        if (!result.version && versionIdentifier) {
          result.version = versionIdentifier;
        }
      } else if (!result.opera && /gecko\//i.test(ua)) {
        result.name = result.name || 'Gecko';
        result.gecko = t;
        result.version = result.version || getFirstMatch(/gecko\/(\d+(\.\d+)?)/i);
      }

      // Check if browser has touch capabilities.
      result.touch = touchScreen;

      // Set OS flags for platforms that have multiple browsers.
      if (!result.msedge && (android || result.silk)) {
        result.android = t;
      } else if (iosdevice) {
        result[iosdevice] = t;
        result.ios = t;
      } else if (mac) {
        result.mac = t;
      } else if (windows) {
        result.windows = t;
      } else if (linux) {
        result.linux = t;
      }

      // OS version extraction.
      let osVersion = '';
      if (result.windowsphone) {
        osVersion = getFirstMatch(/windows phone (?:os)?\s?(\d+(\.\d+)*)/i);
      } else if (iosdevice) {
        osVersion = getFirstMatch(/os (\d+([_\s]\d+)*) like mac os x/i);
        osVersion = osVersion.replace(/[_\s]/g, '.');
      } else if (android) {
        osVersion = getFirstMatch(/android[ /-](\d+(\.\d+)*)/i);
      } else if (result.webos) {
        osVersion = getFirstMatch(/(?:web|hpw)os\/(\d+(\.\d+)*)/i);
      } else if (result.blackberry) {
        osVersion = getFirstMatch(/rim\stablet\sos\s(\d+(\.\d+)*)/i);
      } else if (result.bada) {
        osVersion = getFirstMatch(/bada\/(\d+(\.\d+)*)/i);
      } else if (result.tizen) {
        osVersion = getFirstMatch(/tizen[/\s](\d+(\.\d+)*)/i);
      }
      if (osVersion) {
        result.osversion = osVersion;
        [result.osMajorVersion] = osVersion.split('.');
      }
      if (!mobile && (tablet || iosdevice === 'ipad' || result.silk || android)) {
        result.tablet = tabletPc ? false : t;
      } else if (mobile || iosdevice === 'iphone' || iosdevice === 'ipod' || android || result.blackberry || result.webos || result.bada) {
        result.mobile = t;
      }

      //  Set `nativeApp` flag to true if in web view of mobile app.
      if (/spa_webview/i.test(ua) || /IsMobileApp/i.test(ua)) {
        result.nativeApp = t;
      }
      return result;
    }

    // End Bowser detect.
    // Return the results of the Bowser detect function.
    return detect(uaString);
  }
  // End Bowser function.

  /**
   * Check if browser TLS version is supported.
   * @returns {Boolean} True if TLS is supported (or detection failed), false if not.
   */
  isSupportedTlsVersion() {
    const isSupportedWhenTlsDetectionFails = true;
    const tlsCookieValue = spartaGlobalFunctionsUtility.spartaGetCookie(TLS_COOKIE_NAME);
    if (!tlsCookieValue) return isSupportedWhenTlsDetectionFails;
    const tlsVersionString = tlsCookieValue.replace(TLS_COOKIE_VALUE_VERSION_PREFIX, '');
    if (!tlsVersionString) return isSupportedWhenTlsDetectionFails;
    const tlsVersion = parseFloat(tlsVersionString);
    if (Number.isNaN(tlsVersion)) return isSupportedWhenTlsDetectionFails;
    return tlsVersion >= MIN_SUPPORTED_TLS_VERSION;
  }

  /**
   * System check.
   * @returns {void}
   */
  boaBrowserSystemCheck() {
    // Detect platform by looking at platform or UA.
    const p = this.settings.platform || this.settings.ua;
    const {
      boaBrowserObject
    } = this;
    if (boaBrowserObject) {
      if (p.indexOf('Win') > -1) {
        boaBrowserObject.win = true;
      } else if (p.indexOf('Mac') > -1) {
        boaBrowserObject.mac = true;
      } else if (p.indexOf('X11') > -1 || p.indexOf('Linux') > -1) {
        boaBrowserObject.linux = true;
      } else {
        boaBrowserObject.win = true; // default win
      }

      // Find Windows, Mac, and Linux that was not identified as mobile by Bowser.
      boaBrowserObject.desktop = !boaBrowserObject.mobile && !boaBrowserObject.tablet && (boaBrowserObject.win || boaBrowserObject.mac || boaBrowserObject.linux);
    }

    // Detect Windows operating systems.
    if (boaBrowserObject.win) {
      if (/Win(?:dows )?([^do]{2})\s?(\d+\.\d+)?/.test(this.settings.ua)) {
        if (RegExp.$1 === 'NT') {
          switch (RegExp.$2) {
            case '5.0':
              boaBrowserObject.osversion = '2000';
              break;
            case '5.1':
              boaBrowserObject.osversion = 'XP';
              break;
            case '6.0':
              boaBrowserObject.osversion = 'Vista';
              break;
            case '6.1':
              boaBrowserObject.osversion = 'Windows 7';
              break;
            case '6.2':
              boaBrowserObject.osversion = 'Windows 8';
              break;
            case '10.0':
              boaBrowserObject.osversion = 'Windows 10';
              break;
            case '11.0':
              boaBrowserObject.osversion = 'Windows 11';
              break;
            default:
              boaBrowserObject.osversion = '';
              break;
          }
        } else if (RegExp.$1 === 'Ph') {
          // Windows Phone.
          if (/Windows Phone OS.?(\d+\.\d+)?/.test(this.settings.ua)) {
            boaBrowserObject.osversion = RegExp.$1;
          }
        } else if (RegExp.$1 === '9x') {
          boaBrowserObject.osversion = 'ME';
        } else {
          boaBrowserObject.osversion = RegExp.$1;
        }
      }
    }
    return boaBrowserObject;
  }

  /**
   * Browser plugin detection.
   * @returns {void}
   */
  boaBrowserPluginCheck() {
    const plugins = {};

    /**
     * Plugin detection - doesn't work in IE
     * @param {*} name Plugin name.
     * @returns {boolean} Whether detected.
     */
    function hasPlugin(name) {
      const lowerName = name.toLowerCase();
      for (let i = 0; i < navigator.plugins.length; i += 1) {
        if (navigator.plugins[i].name.toLowerCase().indexOf(lowerName) > -1) {
          return true;
        }
      }
      return false;
    }

    /**
     * Plugin detection for IE.
     * @param {*} name name of plugin
     * @returns {void}
     */
    function hasIEPlugin(name) {
      try {
        // eslint-disable-next-line no-new, no-undef
        new ActiveXObject(name);
        return true;
      } catch (ex) {
        return false;
      }
    }

    /**
     * Detect Flash for all browsers
     * @returns {boolean} Whether detected.
     */
    function hasFlash() {
      let r = hasPlugin('Flash');
      if (!r) {
        r = hasIEPlugin('ShockwaveFlash.ShockwaveFlash');
      }
      return r;
    }

    /**
     * Detect Quicktime for all browsers.
     * @returns {boolean} Whether detected.
     */
    function hasQuickTime() {
      let r = hasPlugin('QuickTime');
      if (!r) {
        r = hasIEPlugin('QuickTime.QuickTime');
      }
      return r;
    }
    try {
      plugins.flash = hasFlash();
      plugins.quicktime = hasQuickTime();
      this.boaBrowserObject.plugins = plugins;
    } catch (err) {
      // Do nothing.
      // eslint-disable-next-line no-console
      console.warn(err);
    }
    // End plugin check.
  }

  /**
   * Make AJAX request to get latest browser content and rules
   * @todo Reimplement this at some point
   * @returns {void}
   */
  boaBrowserUpdateMessageData() {
    this.debug('start boaBrowserUpdateMessageData');
    // Check if we should Ajax in new browser rules.
    // If local, skip Ajax unless `testMode` is on in localhost since it wont exist.
    if (!this.settings.useAjax || this.settings.isLocal && (window.location.protocol === 'file:' || !this.settings.testMode)) {
      this.boaBrowserProcessRules();
      this.boaBrowserObject.loaded = true;
      spartaEventsUtility.pub(EVENTS.LOADED, {});
      return;
    }
    $.ajax({
      type: 'GET',
      // url: '/content/enhanced-splash/js/browserDataCMS.js',
      // TODO: 04/10/2022 - TBD (New endpoint for CMS provided browser data.)
      url: '/globals/utilities/browser-support/1.0.0/browserData.json',
      dataType: 'script',
      cache: true,
      /**
       * Success statement.
       * @returns {void}
       */
      success() {
        try {
          // CMS data loaded successfully.
          // eslint-disable-next-line no-undef
          if (typeof boaBrowserSupportCMS !== 'undefined' && boaBrowserSupportCMS.content) {
            // Update browser object.
            // eslint-disable-next-line no-undef
            this.boaBrowserObject.browserSupport = boaBrowserSupportCMS;

            // Tag this obj as coming from CMS.
            this.boaBrowserObject.browserSupport.source = 'cms';

            // settings.showMessage = boaBrowserSupportCMS.showMessage;

            // Run rules for this browser.
            this.boaBrowserProcessRules();
            this.boaBrowserObject.loaded = true;
            $(document).trigger('spartaBrowserObjectLoaded');
          }
        } catch (e) {
          this.debug(`browserDetect:boaBrowserUpdateMessageData: ajax error: ${e}`);
        }
      }
    });
  }

  /**
   * Run Browser Message Rules & update browser object
   * @returns {void}
   */
  boaBrowserProcessRules() {
    this.debug('browserDetect: begin boaBrowserProcessRules');

    // Run browser rules.
    const userMsg = this.boaBrowserObject.browserSupport.rules(this.boaBrowserObject);

    // If there is a message, sync up global object.
    if (userMsg && userMsg.rule) {
      this.boaBrowserObject.userNotice = userMsg;
    }

    // Once rules are processed, Show message to user. Have to do this here to ensure Ajax call is
    // complete. Only do this after DOM ready.
    this.boaBrowserShowMessage();
  }

  /**
   * Check the properties that would suppress the browser message.
   * Skip if cookie found for msg previously closed
   * || JS var boaBrowserShowNotice == false
   * || URL Param boaBrowserShowNotice == false
   * @returns {void}
   */
  checkShowMessage() {
    try {
      // Don't show browser alert for local work unless in testMode.
      if (this.settings.isLocal && !this.settings.testMode) {
        this.debug('Working in local module / localhost, suppress browser alert');
        return false;
      }
    } catch (e) {
      this.debug(`browserDetect: checkShowMessage error: ${e}`);
    }
    if (!this.settings.showMessage || this.settings.boaBrowserShowNoticeCookie === 'false'
    // eslint-disable-next-line no-undef
    || typeof boaBrowserShowNotice !== 'undefined' && boaBrowserShowNotice === false || spartaGlobalFunctionsUtility.getUrlParam('boaBrowserShowNotice') === 'false') {
      return false;
    }

    // Don't show the message in case of a widget.
    const spaParams = spartaGlobalFunctionsUtility.getSpaParams();
    if (spaParams.isWidget) {
      this.debug('It\'s a widget. Suppress browser alert.');
      return false;
    }

    // Don't show the message if it's already been shown.
    if (document.getElementById('browserUpgradeNoticeBar')) {
      this.debug('The upgrade notice bar was already on the page, so suppress it here.');
      return false;
    }
    this.debug('checkShowMessage OK to show message.');
    return true;
  }

  /**
   * Display browser notification message after doc.ready.
   * @returns {void}
   */
  boaBrowserShowMessage() {
    this.debug('in boaBrowserShowMessage');

    // Check show message again after doc ready.
    this.settings.showMessage = this.checkShowMessage();
    if (!this.settings.showMessage) {
      this.debug('showMessage is false');
      return;
    }
    let link;
    // let currentBrowserInfo = '';
    const b = this.boaBrowserObject;
    const loc = window.location.hostname;
    b.url = b.browserSupport.url || {};

    // Replace `secure` in the URL with `www` for LLE environments.
    if (/(cert|qa|sit|dev|pt|preview|review|pssit).*\.bankofamerica\.com$/.test(loc)) {
      link = `${window.location.protocol}//${loc.replace(/(secure)/, 'www')}`;
    } else {
      link = b.browserSupport.url.prod;
    }
    link = `${link}${b.userNotice.link}`;

    // Check if there is a message to show.
    if (b.userNotice && b.userNotice.rule && b.userNotice.rule !== 'supportedBrowser' && b.userNotice.rule !== 'mobileBrowser') {
      /* if (b.name.length > 0 && b.version.length > 0) {
        currentBrowserInfo = `${b.browserSupport.content.misc.current} ${b.name} ${b.version}<br>`;
      } */

      const {
        style,
        msg,
        btnText
      } = b.userNotice;
      const {
        closeText
      } = b.browserSupport.content.misc;
      const notice = b.browserSupport.template.replace('{currentBrowserInfo}', '').replace('{style}', style).replace('{msg}', msg).replace('{link}', link).replace('{btnText}', btnText).replace('{closeText}', closeText).replace('{msg-icon-sprite-fsd-source}', b.browserSupport.sprites.message);
      $('[data-sparta-container]:first').find('div:first').before(notice);
      if (b.userNotice.isCloseButtonVisible) {
        $('#browserUpgradeNoticeBarClose').show();
      }

      // If user closes the message, don't show again for this session.
      $('#browserUpgradeNoticeBarClose').click(() => {
        $('#browserUpgradeNoticeBar').hide();
        spartaGlobalFunctionsUtility.spartaSetCookie('boaBrowserShowNotice', false);
      });
    }
  }

  /**
   * Check for local storage & retrieve browser obj.
   * @returns {void}
   */
  boaBrowserGetSessionStorage() {
    if (this.settings.useSessionStorage && window.sessionStorage.getItem('settings')) {
      this.settings = JSON.parse(window.sessionStorage.getItem('settings'));
      if (this.settings.boaBrowserObject) {
        return this.settings.boaBrowserObject;
      }
    }
    return false;
  }

  /**
   * Define Default Browser Object.
   * @param {*} callbackFn Function fires on callback
   * @returns {void}
   */
  boaBrowserGetDefaultObj(callbackFn) {
    const notSupportedTlsVersion = !this.isSupportedTlsVersion();
    const boaBrowserDefault = {
      loaded: false,
      ua: this.settings.ua,
      debug: this.settings.debug,
      // Graded support. Used in Bowser.
      a: false,
      c: false,
      x: false,
      // User agent names.
      msie: false,
      edge: false,
      chrome: false,
      firefox: false,
      safari: false,
      opera: false,
      android: false,
      ios: false,
      iosdevice: false,
      ipad: false,
      iphone: false,
      ipod: false,
      blackberry: false,
      silk: false,
      crawler: false,
      // User agent details.
      name: '',
      vendor: '',
      version: '',
      webkit: false,
      gecko: false,
      touch: false,
      // Native app flag.
      nativeApp: false,
      // Device & system, plugins info.
      tablet: false,
      mobile: false,
      desktop: false,
      win: false,
      mac: false,
      linux: false,
      osversion: '',
      windowsphone: false,
      plugins: {
        flash: false,
        quicktime: false
      },
      // Placeholder for user messaging.
      userNotice: {
        rule: '',
        msg: '',
        link: ''
      },
      // Default browser support buckets & content. Kill switch: showMessage = false;
      // ESLint wants getters for this `browserSupport` property.
      browserSupport: {
        rules(b) {
          let userMsg;

          // Mobile browsers.
          if (b.mobile || b.tablet || b.crawler) {
            // No message for any mobile device or bot/crawler.
            userMsg = b.browserSupport.content.mobileBrowser;
          } else if (b.msie && b.version <= VERSIONS.BLOCKED_IE || b.firefox && b.version <= VERSIONS.BLOCKED_FF) {
            // Blocked versions.
            userMsg = b.browserSupport.content.unsupportedBlocked;
          } else if (!(b.win && b.msie) && !b.chrome && !(b.win && b.firefox) && !(b.mac && b.safari)) {
            userMsg = b.browserSupport.content.unsupportedBrowserType;
          } else if (
          // Deprecated! Support End of Life for these browser versions.
          b.msie && b.version <= VERSIONS.EOL_IE // MSIE
          || b.msedge && b.version <= VERSIONS.EOL_EDGE // Edge
          || b.chrome && b.version <= VERSIONS.EOL_CHROME // Chrome
          || b.firefox && b.version <= VERSIONS.EOL_FF // Firefox
          || b.safari && b.version <= VERSIONS.EOL_SAFARI) {
            // Safari
            userMsg = b.browserSupport.content.unsupportedEndOfLife;
          } else if (notSupportedTlsVersion) {
            userMsg = b.browserSupport.content.unsupportedTlsVersion;
          } else {
            userMsg = b.browserSupport.content.supportedBrowser;
          }
          return userMsg;
        },
        template: `
          <div id='browserUpgradeNoticeBar' class='{style}'>
            <div class='row'>
              <div class='column small-11'>
                <div class='msg-icon-sprite-fsd'><span class='ada-hidden'>warning-icon</span></div>
                <div class='msg-container'>
                    <span class='msg-text'>{currentBrowserInfo}{msg}<a target='_blank' href='{link}' name='boaBrowserInfoBtn' id='boaBrowserInfoBtn' class='browser-upgrade-now' rel='noopener noreferrer'>{btnText}</a></span>
                </div>
              </div>
              <div class='column small-1' style='line-height: 18px;'>
                <a id='browserUpgradeNoticeBarClose' class='spa-ui-layer-close' style='top:0; left:0;' href='javascript:void(0);'><span class='close-text ada-hidden'>{closeText} browser upgrade notice</span> <span class='spa-close-x' aria-hidden='true'>×</span></a>
              </div>
            </div>
            <style>
              #browserUpgradeNoticeBar {padding: 10px 0;}
              #browserUpgradeNoticeBarClose .close-fsd {display: inline-block; position: relative; top: 4px;}
              #browserUpgradeNoticeBar.hide-close #browserUpgradeNoticeBarClose {display:none;}
              #browserUpgradeNoticeBar .msg-container { font-size: 14px; line-height: 24px; float: left; padding-left: 10px; }
              #browserUpgradeNoticeBar .msg-icon-sprite-fsd { background: url('{msg-icon-sprite-fsd-source}') no-repeat scroll 0 0 transparent; display: inline-block; float: left;}
              #browserUpgradeNoticeBar .browser-upgrade-now {margin-left: 10px;}
              #browserUpgradeNoticeBar.boa-browser-1 { background-color: #FFF4F9; border-bottom: 1px solid #E31838; }
              #browserUpgradeNoticeBar.boa-browser-1 .msg-icon-sprite-fsd {background-position: -70px 0;height:24px;width:24px;}
              #browserUpgradeNoticeBar.boa-browser-2 { background-color: #FFF5D7; border-bottom: 1px solid #FC7800; }
              #browserUpgradeNoticeBar.boa-browser-2 .msg-icon-sprite-fsd {background-position: -70px -240px;height:24px;width:24px;}
              #browserUpgradeNoticeBar ~ #browserUpgradeNoticeBar {
                display: none !important;
              }
              #browserUpgradeNoticeBar #browserUpgradeNoticeBarClose {
                background-repeat: no-repeat;
                background-position: center;
                background-size: 24px;
                background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwIiB5PSIwIiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAsMCwyNCwyNCI+PHBhdGggZmlsbD0iIzY0NjQ2NCIgc3Ryb2tlPSIiIHN0cm9rZS13aWR0aD0iIiBzdHlsZT0iIiBkPSJNMTguNTg4MjM1Myw0IEwxMiwxMC41ODQxMTUxIEw1LjQxMTc2NDcxLDQgTDQsNS40MTA4ODE4IEwxMC41ODgyMzUzLDExLjk5NDk5NjkgTDQsMTguNTc5MTExOSBMNS40MTE3NjQ3MSwyMCBMMTIsMTMuNDE1ODg0OSBMMTguNTg4MjM1MywyMCBMMjAsMTguNTc5MTExOSBMMTMuNDIxNzc3MiwxMS45OTQ5OTY5IEwyMCw1LjQxMDg4MTggQzIwLDUuNDEwODgxOCAxOS41Mjk0MTE4LDQuOTQwNTg3ODcgMTguNTg4MjM1Myw0IFoiLz48L3N2Zz4=');
                width: 40px;
                height: 40px;
                border: 1px solid transparent;                
                overflow: hidden;
                position: absolute;
                right: 15px;
                top: 0 !important;
                bottom: 0 !important;
                margin: auto !important;
                text-decoration: none;
              }
              #browserUpgradeNoticeBar #browserUpgradeNoticeBarClose:hover,
              #browserUpgradeNoticeBar #browserUpgradeNoticeBarClose:focus {
                border-color: #0053c2;
                background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwIiB5PSIwIiB2aWV3Qm94PSIwLDAsMjQsMjQiPjxwYXRoIGZpbGw9IiMwMDUzYzIiIHN0cm9rZT0iIiBzdHJva2Utd2lkdGg9IiIgc3R5bGU9IiIgZD0iTTE4LjU4ODIzNTMsNCBMMTIsMTAuNTg0MTE1MSBMNS40MTE3NjQ3MSw0IEw0LDUuNDEwODgxOCBMMTAuNTg4MjM1MywxMS45OTQ5OTY5IEw0LDE4LjU3OTExMTkgTDUuNDExNzY0NzEsMjAgTDEyLDEzLjQxNTg4NDkgTDE4LjU4ODIzNTMsMjAgTDIwLDE4LjU3OTExMTkgTDEzLjQyMTc3NzIsMTEuOTk0OTk2OSBMMjAsNS40MTA4ODE4IEMyMCw1LjQxMDg4MTggMTkuNTI5NDExOCw0Ljk0MDU4Nzg3IDE4LjU4ODIzNTMsNCBaIj48L3BhdGg+PC9zdmc+');
              }
              #browserUpgradeNoticeBar #browserUpgradeNoticeBarClose .spa-close-x {
                display: none;
              }
            </style>
          </div>`
      }
    };
    if (!this.importTriggered) {
      this.loadBrowserData().then(xhr => {
        boaBrowserDefault.browserSupport = {
          ...boaBrowserDefault.browserSupport,
          ...xhr.browserSupport
        };
        this.importTriggered = true;
        callbackFn(boaBrowserDefault);
      });
    } else {
      callbackFn(boaBrowserDefault);
    }
  }

  /**
   * Load browser data.
   * @returns {void}
   */
  loadBrowserData() {
    const lang = spartaStoreUtility.getData('lang').split('-')[0];

    // Generate a new `DataAssetLoader` instance.
    const messagingAssetLoader = new DataAssetLoader('@sparta.global.utilities/sparta-browser-support-utility', `data/${lang}`);

    // Load the relevant messages JSON file, which triggers success and failure events so that the
    // utility can subscribe to them.
    // eslint-disable-next-line no-undef
    const load = messagingAssetLoader.loadAsset('browser.json');

    // Generate a promise with the results from our event subscription so we can
    // return the name of the file that was actually loaded.
    // This serves mostly as an example of how to deal with this.
    const assetLoaderPromise = new Promise(resolve => {
      // Subscribe to the event emitted by the DataAssetLoader.
      // We use `true` for the retroactive parameter to make sure that even if we miss
      // the initial event firing due to it happening quicker than expected, we can
      // still respond to it.
      spartaEventsUtility.sub(load.success, evtData => {
        // Define the `messageList` object from the `messages.json` file.
        resolve(evtData.data.xhr);
      }, true);
    });
    spartaEventsUtility.sub(load.failure, evtData => {
      // Log out an error for debugging the issue.
      // eslint-disable-next-line no-console
      if (window.console && console.warn) {
        // eslint-disable-next-line no-console
        console.warn(`Sparta Browser Support: ${evtData.data.filename} AJAX Error.`, evtData.data.xhr);
      }
    }, true);
    return assetLoaderPromise;
  }

  /**
   * _setBrowserCSS - Set Browser CSS classes in HTML based on Browser obj
   * @returns {void}
   */
  addBrowserClasses() {
    let uaClassesStr = '';
    const uaClasses = [];
    const classPrefix = 'spa-ua-';
    const b = this.boaBrowserObject || {};

    // Class for device.
    switch (true) {
      case b.desktop:
        uaClasses.push('desktop');
        break;
      case b.tablet:
        uaClasses.push('tablet');
        break;
      case b.mobile:
        uaClasses.push('mobile');
        break;
    }

    // Add touch class.
    if (b.touch) {
      uaClasses.push('touch');
    }

    // Class for browser.
    switch (true) {
      case b.msie:
        uaClasses.push('browser-msie');
        break;
      case b.msedge:
        uaClasses.push('browser-msedge');
        break;
      case b.firefox:
        uaClasses.push('browser-firefox');
        break;
      case b.chrome:
        uaClasses.push('browser-chrome');
        break;
      case b.safari:
        uaClasses.push('browser-safari');
        break;
      case b.opera:
        uaClasses.push('browser-opera');
        break;
      case b.silk:
        uaClasses.push('browser-silk');
        break;
      default:
        uaClasses.push('browser-other');
    }
    if (b.version) {
      uaClasses.push(`browser-version-${parseInt(b.version, 10)}`);
    }

    // Class for OS/device/system type.
    switch (true) {
      case b.mac:
        uaClasses.push('system-mac');
        break;
      case b.ios:
        uaClasses.push('system-ios');
        switch (true) {
          case b.ipad:
            uaClasses.push('system-ipad');
            break;
          case b.iphone:
            uaClasses.push('system-iphone');
            break;
          case b.ipod:
            uaClasses.push('system-ipod');
            break;
        }
        break;
      case b.android:
        uaClasses.push('system-android');
        break;
      case b.windowsphone:
        uaClasses.push('system-windowsphone');
        break;
      case b.blackberry:
        uaClasses.push('system-blackberry');
        break;
      case b.win:
        uaClasses.push('system-win');
        break;
      default:
        uaClasses.push('system-other');
    }
    for (let i = 0; i < uaClasses.length; i += 1) {
      uaClassesStr = `${uaClassesStr} ${classPrefix}${uaClasses[i]}`;
    }
    $(document.documentElement).addClass(uaClassesStr);
  }
}
var index = new SpartaBrowserSupportUtility();

export { index as default };
